import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, CalendarClock, CheckCircle2, AlertCircle, Eye } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useUserFormularios } from "@/hooks/useUserFormularios";
import { PageLoading } from "@/components/ui/page-loading";
import { UserFormularioRenderer } from "./UserFormularioRenderer";

export function UserFormulariosList() {
  const { formulariosPendentes, formulariosRespondidos, formulariosVencidos, isLoading, marcarComoLido, podeResponder } = useUserFormularios();
  const [selectedNotificacao, setSelectedNotificacao] = useState<any>(null);
  const [isResponding, setIsResponding] = useState(false);

  const handleResponder = async (notificacao: any) => {
    await marcarComoLido(notificacao.id);
    setSelectedNotificacao(notificacao);
    setIsResponding(true);
  };

  const handleVoltar = () => {
    setIsResponding(false);
    setSelectedNotificacao(null);
  };

  if (isResponding && selectedNotificacao) {
    return (
      <UserFormularioRenderer
        notificacao={selectedNotificacao}
        onVoltar={handleVoltar}
      />
    );
  }

  if (isLoading) {
    return <PageLoading />;
  }

  const renderFormularioCard = (notificacao: any, tipo: 'pendente' | 'respondido' | 'vencido') => {
    const formulario = notificacao.formulario;
    const distribuicao = notificacao.distribuicao;
    const resposta = notificacao.resposta;

    return (
      <Card key={notificacao.id} className="hover:shadow-md transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {formulario?.titulo}
              </CardTitle>
              {formulario?.descricao && (
                <CardDescription className="mt-2">{formulario.descricao}</CardDescription>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {distribuicao?.obrigatorio && (
                <Badge variant="destructive">Obrigatório</Badge>
              )}
              {tipo === 'pendente' && <Badge variant="default">Pendente</Badge>}
              {tipo === 'respondido' && <Badge variant="secondary">Respondido</Badge>}
              {tipo === 'vencido' && <Badge variant="outline" className="border-red-500 text-red-500">Vencido</Badge>}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {distribuicao?.data_limite && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CalendarClock className="h-4 w-4" />
                <span>
                  Prazo: {format(new Date(distribuicao.data_limite), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                </span>
                {tipo === 'pendente' && new Date(distribuicao.data_limite) < new Date() && (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                )}
              </div>
            )}

            {distribuicao?.mensagem_personalizada && (
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium mb-1">Mensagem:</p>
                <p>{distribuicao.mensagem_personalizada}</p>
              </div>
            )}

            {tipo === 'respondido' && resposta && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                <span>
                  Respondido em {format(new Date(resposta.data_conclusao || notificacao.criado_em), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                </span>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              {tipo === 'pendente' && podeResponder(notificacao) && (
                <Button onClick={() => handleResponder(notificacao)} className="flex-1">
                  <FileText className="h-4 w-4 mr-2" />
                  Responder Formulário
                </Button>
              )}
              {tipo === 'respondido' && resposta && (
                <Button variant="outline" onClick={() => handleResponder(notificacao)} className="flex-1">
                  <Eye className="h-4 w-4 mr-2" />
                  Ver Resposta
                </Button>
              )}
              {tipo === 'vencido' && (
                <div className="flex-1 text-center text-sm text-muted-foreground py-2">
                  Prazo de resposta expirado
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Meus Formulários</h2>
        <p className="text-muted-foreground">
          Formulários atribuídos para você responder
        </p>
      </div>

      <Tabs defaultValue="pendentes" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pendentes">
            Pendentes
            {formulariosPendentes.length > 0 && (
              <Badge className="ml-2" variant="default">
                {formulariosPendentes.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="respondidos">
            Respondidos
            {formulariosRespondidos.length > 0 && (
              <Badge className="ml-2" variant="secondary">
                {formulariosRespondidos.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="vencidos">
            Vencidos
            {formulariosVencidos.length > 0 && (
              <Badge className="ml-2" variant="outline">
                {formulariosVencidos.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendentes" className="space-y-4">
          {formulariosPendentes.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  Nenhum formulário pendente
                </div>
              </CardContent>
            </Card>
          ) : (
            formulariosPendentes.map((n) => renderFormularioCard(n, 'pendente'))
          )}
        </TabsContent>

        <TabsContent value="respondidos" className="space-y-4">
          {formulariosRespondidos.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  Nenhum formulário respondido ainda
                </div>
              </CardContent>
            </Card>
          ) : (
            formulariosRespondidos.map((n) => renderFormularioCard(n, 'respondido'))
          )}
        </TabsContent>

        <TabsContent value="vencidos" className="space-y-4">
          {formulariosVencidos.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  Nenhum formulário vencido
                </div>
              </CardContent>
            </Card>
          ) : (
            formulariosVencidos.map((n) => renderFormularioCard(n, 'vencido'))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
